import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday } from 'date-fns';
import {
  FiClock, FiCalendar, FiMoreVertical, FiSearch, FiX, FiHash, FiLock, FiUsers, FiFileText,
  FiTag, FiSettings, FiUser, FiPlus, FiLogOut, FiEdit
} from 'react-icons/fi';
import { useAuthStore } from '../../store/authStore';
import { useChatStore } from '../../store/chatStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import { disconnectSocket } from '../../services/socket';
import CreateChannelModal from '../Sidebar/CreateChannelModal.jsx';
import DirectMessageModal from '../Sidebar/DirectMessageModal.jsx';
import LogoutConfirmModal from '../Sidebar/LogoutConfirmModal.jsx';
import Logo from '../../assets/LogoBlack.png';
import './MobileChatHome.css';

const authSelector = (state) => ({ user: state.user, logout: state.logout });
const chatSelector = (state) => ({ channels: state.channels, setCurrentChannel: state.setCurrentChannel });

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'chats', label: 'Chats' },
  { key: 'channels', label: 'Channels' },
];

const CHANNEL_ICONS = { private: FiLock, project: FiUsers, eod: FiFileText };

function formatChatTime(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'dd/MM/yy');
}

function MobileChatHome() {
  const { user, logout } = useShallowStore(useAuthStore, authSelector);
  const { channels, setCurrentChannel } = useShallowStore(useChatStore, chatSelector);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDMModal, setShowDMModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const menuRef = useRef(null);

  const role = user?.role;
  const currentUserId = String(user?._id || user?.id || '');
  const isAttendanceAdmin = ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'attendance_manager'].includes(role);

  // Close the ⋮ menu on outside tap
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menuOpen]);

  const chats = useMemo(() => channels
    .map((channel) => {
      const isDirect = channel.type === 'direct';
      const otherMember = isDirect
        ? channel.members?.find(m => {
          const memberId = String(m._id || m.id || '');
          return memberId !== currentUserId && memberId !== '';
        })
        : null;
      const name = isDirect
        ? (otherMember ? `${otherMember.firstName || ''} ${otherMember.lastName || ''}`.trim() || otherMember.username || 'Unknown User' : 'Unknown User')
        : channel.name;
      const lastMsg = channel.lastMessage;
      const lastSenderId = String(lastMsg?.sender?._id || lastMsg?.sender || '');
      return {
        channel,
        isDirect,
        otherMember,
        name,
        preview: lastMsg?.content ? `${lastSenderId === currentUserId ? 'You: ' : ''}${lastMsg.content}` : (isDirect ? '' : channel.description || ''),
        time: lastMsg?.createdAt || channel.lastActivity,
        unread: channel.userUnreadCount || 0,
      };
    })
    .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0)), [channels, currentUserId]);

  const unreadTotal = useMemo(() => chats.filter(c => c.unread > 0).length, [chats]);

  const visibleChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats.filter((c) => {
      if (filter === 'unread' && c.unread === 0) return false;
      if (filter === 'chats' && !c.isDirect) return false;
      if (filter === 'channels' && c.isDirect) return false;
      return !q || c.name.toLowerCase().includes(q);
    });
  }, [chats, filter, search]);

  const openChat = (channel) => {
    setCurrentChannel(channel);
    navigate(`/dashboard/channel/${channel._id}`);
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    disconnectSocket();
    await logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  const menuItems = [
    { label: 'My Profile', icon: FiUser, onClick: () => go('/dashboard/profile') },
    ['ceo', 'co_ceo', 'company_manager'].includes(role) && { label: 'Admin Panel', icon: FiSettings, onClick: () => go('/dashboard/admin') },
    ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'manager'].includes(role) && { label: 'Projects', icon: FiUsers, onClick: () => go('/dashboard/projects') },
    { label: 'Tickets', icon: FiTag, onClick: () => go('/dashboard/tickets') },
    { label: 'EOD Reports', icon: FiFileText, onClick: () => go('/dashboard/eod') },
    role !== 'employee' && { label: 'New Channel', icon: FiPlus, onClick: () => { setMenuOpen(false); setShowCreateModal(true); } },
    { label: 'Logout', icon: FiLogOut, danger: true, onClick: () => { setMenuOpen(false); setShowLogoutModal(true); } },
  ].filter(Boolean);

  return (
    <div className="mch">
      <header className="mch-topbar">
        <img src={Logo} alt="Bigwals" className="mch-logo" />
        <div className="mch-top-actions">
          <button className="mch-icon-btn" onClick={() => go('/dashboard/attendance')} aria-label={isAttendanceAdmin ? 'Attendance' : 'My Attendance'}>
            <FiClock size={21} />
          </button>
          <button className="mch-icon-btn" onClick={() => go('/dashboard/leaves')} aria-label="Leaves">
            <FiCalendar size={21} />
          </button>
          <div className="mch-menu-wrap" ref={menuRef}>
            <button className="mch-icon-btn" onClick={() => setMenuOpen(o => !o)} aria-label="More options" aria-expanded={menuOpen}>
              <FiMoreVertical size={21} />
            </button>
            {menuOpen && (
              <div className="mch-menu" role="menu">
                {menuItems.map(({ label, icon: Icon, onClick, danger }) => (
                  <button key={label} className={`mch-menu-item ${danger ? 'danger' : ''}`} onClick={onClick} role="menuitem">
                    <Icon size={18} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="mch-search">
        <FiSearch size={18} />
        <input
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="mch-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
            <FiX size={18} />
          </button>
        )}
      </div>

      <div className="mch-filters" role="tablist">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            aria-selected={filter === key}
            className={`mch-chip ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {label}
            {key === 'unread' && unreadTotal > 0 && <span className="mch-chip-count">{unreadTotal}</span>}
          </button>
        ))}
      </div>

      <div className="mch-list">
        {visibleChats.length === 0 ? (
          <div className="mch-empty">
            {search ? 'No chats match your search' : filter === 'unread' ? 'No unread chats' : 'No chats yet'}
          </div>
        ) : visibleChats.map(({ channel, isDirect, otherMember, name, preview, time, unread }) => {
          const ChannelIcon = CHANNEL_ICONS[channel.type] || FiHash;
          return (
            <button key={channel._id} className={`mch-row ${unread > 0 ? 'unread' : ''}`} onClick={() => openChat(channel)}>
              <div className="mch-avatar-wrap">
                {isDirect && otherMember?.profilePicture ? (
                  <img src={otherMember.profilePicture} alt="" className="mch-avatar" />
                ) : isDirect ? (
                  <div className="mch-avatar mch-avatar-initials">
                    {otherMember?.firstName?.[0]}{otherMember?.lastName?.[0]}
                  </div>
                ) : (
                  <div className="mch-avatar mch-avatar-channel"><ChannelIcon size={22} /></div>
                )}
                {isDirect && otherMember?.status === 'online' && <span className="mch-online" />}
              </div>
              <div className="mch-row-body">
                <div className="mch-row-top">
                  <span className="mch-name">{name}</span>
                  <span className="mch-time">{formatChatTime(time)}</span>
                </div>
                <div className="mch-row-bottom">
                  <span className="mch-preview">{preview}</span>
                  {unread > 0 && <span className="mch-badge">{unread > 99 ? '99+' : unread}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button className="mch-fab" onClick={() => setShowDMModal(true)} aria-label="New message">
        <FiEdit size={22} />
      </button>

      {showCreateModal && <CreateChannelModal onClose={() => setShowCreateModal(false)} />}
      {showDMModal && <DirectMessageModal onClose={() => setShowDMModal(false)} />}
      {showLogoutModal && (
        <LogoutConfirmModal onConfirm={handleLogout} onCancel={() => setShowLogoutModal(false)} />
      )}
    </div>
  );
}

export default MobileChatHome;
